import { recordConsoleError } from "@/lib/observability/client-diagnostics";
import { normalizeConsoleError } from "@/lib/observability/normalize-error";
import { sanitizeUnknown } from "@/lib/observability/sanitize";
import {
  getOriginalConsoleError,
  setOriginalConsoleError,
} from "@/lib/observability/console-base";

const SERVER_HOOK_INSTALLED_SYMBOL = Symbol.for(
  "vidseeds.observability.server_console_hook_installed"
);
const CLIENT_HOOK_INSTALLED_SYMBOL = Symbol.for(
  "vidseeds.observability.client_console_hook_installed"
);
const CONSOLE_HOOK_RUNNING_SYMBOL = Symbol.for(
  "vidseeds.observability.console_hook_running"
);

type GlobalHookState = typeof globalThis & {
  [SERVER_HOOK_INSTALLED_SYMBOL]?: boolean;
  [CLIENT_HOOK_INSTALLED_SYMBOL]?: boolean;
  [CONSOLE_HOOK_RUNNING_SYMBOL]?: boolean;
};

function ensureOriginalConsoleStored(): void {
  setOriginalConsoleError(console.error.bind(console));
}

/** Messages from third-party runtimes, browser quirks, and framework noise. */
const IGNORED_CONSOLE_PATTERNS = [
  // WASM / TensorFlow runtime
  "Created TensorFlow Lite XNNPACK delegate",
  "TfLiteWebModelRunner",
  "OpenGL error checking is disabled",
  "gl_context.cc",
  "inference_feedback_manager.cc",
  "Feedback manager requires a model with a single signature",
  "non-portable extension enabled",
  // Browser quirks
  "ResizeObserver loop",
  // Sentry SDK (prevent feedback loops when Sentry itself errors)
  "Failed to load resource",
  "[Sentry]",
  // Third-party analytics SDK errors — external services, not actionable
  "Amplitude Logger",
  // React 19 dev-mode strict validation warnings — these only fire in
  // development builds and are not real errors (NODE-1XB, NODE-1P2).
  "An empty string",
  "was passed to the",
  // REGRESSION GUARD — NODE-22G, NODE-1ZB, NODE-1T3 (regressed 2x each)
  // Routes that already report via explicit reportWarning/reportServerError
  // with correct severity. The console.error call is for kubectl logs only —
  // the console hook must NOT re-report it at error level.
  // DO NOT: remove these patterns without also removing the console.error calls
  // in the corresponding routes.
  "[client-error-report]",
  // REGRESSION GUARD — NODE-27K (duplicate of NODE-268)
  // tracked-fetch.ts:209 calls console.error("[tracked-fetch] ...") for
  // kubectl/devtools logging, then reports directly via reportClientError().
  // Without this filter, the console hook re-captures the same error as a
  // second Sentry issue (NODE-27K = NODE-268 duplicate).
  // DO NOT: remove without also removing the console.error in reportFetchError.
  "[tracked-fetch]",
  "[gemini] Grounding request failed",
  // SENTRY FIX: NODE-1F6 — Better Auth invalid_grant console.error (REGRESSION)
  // Date: 2026-04-15 | Agent: Claude Code
  // Previous attempts: None targeted this directly; the error kept regressing.
  // Root cause: Better Auth's internal logger calls console.error for expected
  //   OAuth failures (expired auth codes, double-clicks). The callback handler
  //   already redirects users to /login?error=invalid_code. This console.error
  //   gets captured by the console hook and reported to Sentry as NODE-1F6.
  // Fix: Suppress Better Auth's expected OAuth error logging from Sentry.
  //   The callback route handler already handles user experience correctly.
  // DO NOT: Remove — invalid_grant is expected user-driven behavior.
  "[Better Auth]",
];

const NODE_PROCESS_WARNING_RE =
  /^\(node:\d+\)\s*\[DEP\d+\]\s*DeprecationWarning/i;

function isIgnoredConsoleMessage(args: unknown[]): boolean {
  const first = typeof args[0] === "string" ? args[0] : "";
  // SENTRY FIX: VIDSEEDS-2JX — Node process warnings re-reported by console hook
  // Date: 2026-04-23 | Agent: Codex
  // Root cause: sentry.server.config.ts drops DEP#### process warnings in
  //   beforeSend, but instrumentation installs this console.error hook before
  //   Sentry init. The hook converted the same Node advisory into a new
  //   reportServerError event before Sentry's process-warning guard could run.
  // Fix: keep the warning in pod stderr via original(...args), but do not
  //   synthesize an application error from Node's precise DEP#### prefix.
  if (NODE_PROCESS_WARNING_RE.test(first)) {
    return true;
  }
  return IGNORED_CONSOLE_PATTERNS.some((p) => first.includes(p));
}

/**
 * Node/Next emit console.error when the client closes the socket before the
 * response is fully written (navigation away, duplicate fetch abort, etc.).
 * Stack traces point at abortIncoming/socketOnClose — not an app bug.
 */
function isBenignIncomingRequestAbort(args: unknown[]): boolean {
  for (const arg of args) {
    if (!(arg instanceof Error)) continue;
    const errno = arg as NodeJS.ErrnoException;
    const stack = arg.stack ?? "";
    if (arg.message !== "aborted") continue;
    if (
      errno.code === "ECONNRESET" ||
      stack.includes("abortIncoming") ||
      stack.includes("socketOnClose")
    ) {
      return true;
    }
  }
  return false;
}

function shouldSkipHook(): boolean {
  const state = globalThis as GlobalHookState;
  return state[CONSOLE_HOOK_RUNNING_SYMBOL] === true;
}

function setHookRunning(value: boolean): void {
  const state = globalThis as GlobalHookState;
  state[CONSOLE_HOOK_RUNNING_SYMBOL] = value;
}

// Rate-limit console.error reports per unique error signature.
// Prevents burst console errors (e.g. React render loops) from flooding Sentry,
// while ensuring distinct critical errors are never starved out by noise.
const CONSOLE_REPORT_CAP = 10;
const consoleReportCounts = new Map<string, number>();
let consoleWindowStart = Date.now();

function getErrorSignature(args: unknown[]): string {
  try {
    const str = args
      .map((a) => (a instanceof Error ? a.message : String(a)))
      .join(" ");
    return str.replace(/\d+/g, "").slice(0, 150); // Remove numbers and truncate to group similar errors
  } catch {
    return "unknown_error_signature";
  }
}

function isConsoleReportAllowed(args: unknown[]): boolean {
  const now = Date.now();
  if (now - consoleWindowStart > 60_000) {
    consoleReportCounts.clear();
    consoleWindowStart = now;
  }

  const signature = getErrorSignature(args);
  const count = (consoleReportCounts.get(signature) || 0) + 1;
  consoleReportCounts.set(signature, count);

  return count <= CONSOLE_REPORT_CAP;
}

export function installServerConsoleErrorHook(): void {
  if (typeof window !== "undefined") return;

  const state = globalThis as GlobalHookState;
  if (state[SERVER_HOOK_INSTALLED_SYMBOL]) return;

  ensureOriginalConsoleStored();
  const original = getOriginalConsoleError();

  console.error = (...args: unknown[]) => {
    original(...args);

    if (
      shouldSkipHook() ||
      isIgnoredConsoleMessage(args) ||
      isBenignIncomingRequestAbort(args) ||
      !isConsoleReportAllowed(args)
    )
      return;
    setHookRunning(true);

    try {
      const error = normalizeConsoleError(args);
      import("@/lib/observability/report-server-error")
        .then(({ reportServerError }) => {
          reportServerError(error, {
            source: "console.error",
            severity: "error",
            extra: {
              consoleArgs: sanitizeUnknown(args),
            },
          });
        })
        .catch((err) => {
          original(
            "[observability] Promise catch in lib/observability/console-hooks.ts",
            err
          );
        })
        .finally(() => {
          setHookRunning(false);
        });
    } catch (err) {
      original(
        "[observability] Caught error in lib/observability/console-hooks.ts",
        err
      );
      setHookRunning(false);
    }
  };

  state[SERVER_HOOK_INSTALLED_SYMBOL] = true;
}

export function installClientConsoleErrorHook(): void {
  if (typeof window === "undefined") return;

  const state = globalThis as GlobalHookState;
  if (state[CLIENT_HOOK_INSTALLED_SYMBOL]) return;

  ensureOriginalConsoleStored();
  const original = getOriginalConsoleError();

  console.error = (...args: unknown[]) => {
    original(...args);

    // Buffer for Report Problem diagnostics (always, regardless of rate limiting)
    recordConsoleError(
      args
        .map((a) => (typeof a === "string" ? a : String(a)))
        .join(" ")
        .slice(0, 500)
    );

    if (
      shouldSkipHook() ||
      isIgnoredConsoleMessage(args) ||
      !isConsoleReportAllowed(args)
    )
      return;
    setHookRunning(true);

    try {
      const error = normalizeConsoleError(args);
      import("@/lib/observability/report-client-error")
        .then(({ reportClientError }) => {
          reportClientError(error, {
            source: "console.error",
            severity: "error",
            extra: {
              consoleArgs: sanitizeUnknown(args),
            },
          });
        })
        .catch((err) => {
          original(
            "[observability] Promise catch in lib/observability/console-hooks.ts",
            err
          );
        })
        .finally(() => {
          setHookRunning(false);
        });
    } catch (err) {
      original(
        "[observability] Caught error in lib/observability/console-hooks.ts",
        err
      );
      setHookRunning(false);
    }
  };

  state[CLIENT_HOOK_INSTALLED_SYMBOL] = true;
}
